function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }


import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useEffect } from 'react';
const styles = require('./ThemeContext-styles.module.scss');
const ThemeContext = React.createContext('dark');
export default ThemeContext;

/**
 * Provides a theme context and passes through a CSS class for the appropriate
 * styles. Generally shouldn't used directly, but helpful if you need to swap
 * contexts dynamically.
 *
 * @private
 */
export const ThemeProvider = ({
  theme,
  children,
  ...props
}) => React.createElement(ThemeContext.Provider, {
  value: theme
}, React.cloneElement(children, { ...props,
  className: classNames(children.props.className, styles[theme])
}));
ThemeProvider.propTypes = {
  theme: PropTypes.oneOf(['light', 'dark']).isRequired
};
/**
 * Changes the active theme context, components that provide a specific
 * background color (such as a Modal) should explicitly provide the theme which
 * works with the background.
 *
 * @param {'dark' | 'light'} theme A new active theme context
 */

export const provideTheme = theme => {
  function hoc(Component) {
    return React.forwardRef((props, ref) => React.createElement(ThemeProvider, {
      theme: theme
    }, React.createElement(Component, _extends({}, props, {
      ref: ref
    }))));
  }

  return hoc;
};
/**
 * Hook that returns the given theme if passed,
 * falling back to the current active theme context.
 */

export function useTheme(theme) {
  const themeContext = useContext(ThemeContext);
  return theme || themeContext;
}

/**
 * A higher order component that provides the current active theme context to a
 * component for use in picking theme appropriate styles.
 *
 * @param {Component} Component
 */
export function withThemeContext(Component) {
  const ComponentWithThemeContext = React.forwardRef((props, ref) => {
    const theme = useTheme(props.theme);
    return React.createElement(Component, _extends({}, props, {
      ref: ref,
      theme: theme
    }));
  });
  ComponentWithThemeContext.displayName = `withThemeContext(${Component.name || Component.displayName})`;
  return ComponentWithThemeContext;
}
/**
 * Hook to apply a global theme when a component is mounted.
 * @param {Theme} theme
 */

export function useGlobalTheme(theme) {
  // FIXME: This doesn't actually change the context value,
  // so this needs to be paired with ThemeProvider.
  useEffect(() => {
    document.body.classList.add(styles[theme]);
    return () => {
      document.body.classList.remove(styles[theme]);
    };
  }, [theme]);
}